import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {concat, Observable, of} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import {environment} from '../../../../../../environments/environment';
import {AbstractHttpService} from '../../../../../service/abstract-http-service';
import {QualifyingRuleSourceDetail} from '../models/qualifying-rule-source-detail.model';
import {
  QualifyingRuleSourceFilters,
  QualifyingRuleSourcePage
} from '../models/qualifying-rule-source.model';

export enum QualifyingRuleSourceDiscoveryStatus {
  Loading = 'loading',
  Empty = 'empty',
  Success = 'success',
  Error = 'error'
}

export interface QualifyingRuleSourceDiscoveryRequest {
  readonly accountId: number;
  readonly filters: QualifyingRuleSourceFilters;
}

export interface QualifyingRuleSourceDiscoveryState {
  readonly status: QualifyingRuleSourceDiscoveryStatus;
  readonly page: QualifyingRuleSourcePage | null;
  readonly error: string | null;
  readonly requestId: number;
}

interface IdentifiedDiscoveryRequest {
  readonly request: QualifyingRuleSourceDiscoveryRequest;
  readonly requestId: number;
}

@Injectable({
  providedIn: 'root'
})
export class QualifyingRuleSourceService extends AbstractHttpService {
  private static readonly apiPath = '/api/offer-sets/qualifying-rule-sources';
  private static readonly debounceMilliseconds = 300;
  private static readonly genericErrorMessage = 'Unable to load qualifying rule sources.';
  private readonly requestOptions = {
    headers: this.acceptJsonHeaders,
    withCredentials: true
  };
  private latestRequestId = 0;

  constructor(private readonly httpClient: HttpClient)
  {
    super();
  }

  public getQualifyingRuleSourcePage(
    accountId: number,
    filters: QualifyingRuleSourceFilters
  ): Observable<QualifyingRuleSourcePage>
  {
    this.validateIdentifier(accountId, 'accountId');
    const url = this.buildPageUrl(accountId);
    const params = this.buildPageParams(filters);
    const response = this.httpClient.get<QualifyingRuleSourcePage>(
      url,
      {...this.requestOptions, params}
    ).pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));

    return response;
  }

  public getQualifyingRuleSourceDetail(
    accountId: number,
    offerSetId: number,
    filters: QualifyingRuleSourceFilters
  ): Observable<QualifyingRuleSourceDetail>
  {
    this.validateIdentifier(accountId, 'accountId');
    this.validateIdentifier(offerSetId, 'offerSetId');
    const url = this.buildDetailUrl(accountId, offerSetId);
    const params = this.buildEligibilityParams(filters);
    const response = this.httpClient.get<QualifyingRuleSourceDetail>(
      url,
      {...this.requestOptions, params}
    ).pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));

    return response;
  }

  public discover(
    requests: Observable<QualifyingRuleSourceDiscoveryRequest>
  ): Observable<QualifyingRuleSourceDiscoveryState>
  {
    const states = requests.pipe(
      distinctUntilChanged((previous, current) => this.requestsMatch(previous, current)),
      map((request) => this.identifyRequest(request)),
      debounceTime(QualifyingRuleSourceService.debounceMilliseconds),
      switchMap((request) => this.createDiscoveryRequest(request)),
      filter((state) => this.isCurrentState(state))
    );

    return states;
  }

  private createDiscoveryRequest(
    identifiedRequest: IdentifiedDiscoveryRequest
  ): Observable<QualifyingRuleSourceDiscoveryState>
  {
    const request = identifiedRequest.request;
    const requestId = identifiedRequest.requestId;
    const loadingState = this.buildLoadingState(requestId);
    const resultState = this.getQualifyingRuleSourcePage(request.accountId, request.filters).pipe(
      map((page) => this.buildSuccessState(page, requestId)),
      catchError((error: unknown) => of(this.buildErrorState(error, requestId)))
    );
    const states = concat(of(loadingState), resultState);

    return states;
  }

  private identifyRequest(request: QualifyingRuleSourceDiscoveryRequest): IdentifiedDiscoveryRequest
  {
    this.latestRequestId += 1;
    const identifiedRequest: IdentifiedDiscoveryRequest = {
      request,
      requestId: this.latestRequestId
    };

    return identifiedRequest;
  }

  private buildPageParams(filters: QualifyingRuleSourceFilters): HttpParams
  {
    let params = this.buildEligibilityParams(filters);
    params = params.set('page', filters.page.toString());
    params = params.set('pageSize', filters.pageSize.toString());
    params = params.set('maximumPriceSortDirection', filters.maximumPriceSortDirection);
    params = this.setOptionalString(params, 'percentageDiscount', filters.percentageDiscount ?? null);
    if (filters.percentageDiscount != null) {
      params = params.set('discountBasis', filters.discountBasis ?? 'selling_price');
    }

    return params;
  }

  private buildEligibilityParams(filters: QualifyingRuleSourceFilters): HttpParams
  {
    let params = new HttpParams();
    params = params.set('vehicleType', filters.vehicleType);
    params = params.set('privacy', filters.privacy);
    params = this.setOptionalNumber(params, 'year', filters.year);
    params = this.setOptionalString(params, 'make', filters.make);
    params = this.setOptionalString(params, 'model', filters.model);
    params = this.setOptionalString(params, 'trim', filters.trim);
    params = this.setOptionalNumber(params, 'minimumPrice', filters.minimumPrice);
    params = this.setOptionalNumber(params, 'maximumPrice', filters.maximumPrice);

    return params;
  }

  private setOptionalNumber(params: HttpParams, key: string, value: number | null): HttpParams
  {
    let resolvedParams = params;
    if (value !== null) {
      resolvedParams = resolvedParams.set(key, value.toString());
    }

    return resolvedParams;
  }

  private setOptionalString(params: HttpParams, key: string, value: string | null): HttpParams
  {
    let resolvedParams = params;
    if (value !== null) {
      resolvedParams = resolvedParams.set(key, value);
    }

    return resolvedParams;
  }

  private validateIdentifier(identifier: number, name: string): void
  {
    if (!Number.isInteger(identifier) || identifier <= 0) {
      throw new RangeError(`${name} must be a positive integer.`);
    }
  }

  private buildPageUrl(accountId: number): string
  {
    const url = `${this.buildBaseUrl()}/${accountId}`;

    return url;
  }

  private buildDetailUrl(accountId: number, offerSetId: number): string
  {
    const url = `${this.buildBaseUrl()}/${accountId}/${offerSetId}`;

    return url;
  }

  private buildBaseUrl(): string
  {
    const url = `//${environment.autoDomain}:${environment.autoPort}${QualifyingRuleSourceService.apiPath}`;

    return url;
  }

  private requestsMatch(
    previous: QualifyingRuleSourceDiscoveryRequest,
    current: QualifyingRuleSourceDiscoveryRequest
  ): boolean
  {
    const previousKey = this.buildRequestKey(previous);
    const currentKey = this.buildRequestKey(current);
    const matches = previousKey === currentKey;

    return matches;
  }

  private buildRequestKey(request: QualifyingRuleSourceDiscoveryRequest): string
  {
    const filters = request.filters;
    const values = [
      request.accountId,
      filters.year,
      filters.make,
      filters.model,
      filters.trim,
      filters.minimumPrice,
      filters.maximumPrice,
      filters.vehicleType,
      filters.privacy,
      filters.maximumPriceSortDirection,
      filters.percentageDiscount ?? null,
      filters.discountBasis ?? 'selling_price',
      filters.page,
      filters.pageSize
    ];
    const key = JSON.stringify(values);

    return key;
  }

  private buildLoadingState(requestId: number): QualifyingRuleSourceDiscoveryState
  {
    const state: QualifyingRuleSourceDiscoveryState = {
      status: QualifyingRuleSourceDiscoveryStatus.Loading,
      page: null,
      error: null,
      requestId
    };

    return state;
  }

  private buildSuccessState(
    page: QualifyingRuleSourcePage,
    requestId: number
  ): QualifyingRuleSourceDiscoveryState
  {
    let status = QualifyingRuleSourceDiscoveryStatus.Success;
    if (page.items.length === 0) {
      status = QualifyingRuleSourceDiscoveryStatus.Empty;
    }
    const state: QualifyingRuleSourceDiscoveryState = {
      status,
      page,
      error: null,
      requestId
    };

    return state;
  }

  private buildErrorState(error: unknown, requestId: number): QualifyingRuleSourceDiscoveryState
  {
    const state: QualifyingRuleSourceDiscoveryState = {
      status: QualifyingRuleSourceDiscoveryStatus.Error,
      page: null,
      error: this.resolveErrorMessage(error),
      requestId
    };

    return state;
  }

  private resolveErrorMessage(error: unknown): string
  {
    let message = QualifyingRuleSourceService.genericErrorMessage;
    if (typeof error === 'string' && error.trim() !== '') {
      message = error;
    }
    if (error instanceof Error && error.message.trim() !== '') {
      message = error.message;
    }

    return message;
  }

  private isCurrentState(state: QualifyingRuleSourceDiscoveryState): boolean
  {
    const isCurrent = state.requestId === this.latestRequestId;

    return isCurrent;
  }
}
